// Collection grid helpers: the "Load more" button with its progress bar, and the floating filter button.
// Both are custom elements so they re-initialise by themselves when Dawn's facets replace #ProductGridContainer.

if (!customElements.get('collection-load-more')) {
  customElements.define(
    'collection-load-more',
    class CollectionLoadMore extends HTMLElement {
      connectedCallback() {
        this.loading = false;
        this.onClick = (event) => {
          const link = event.target.closest('[data-lm-button]');
          if (!link) return;
          // Let the browser handle modified clicks (new tab, etc.) with the plain href.
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          this.loadMore();
        };
        this.addEventListener('click', this.onClick);
        this.syncHref();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
      }

      get button() {
        return this.querySelector('[data-lm-button]');
      }

      get nextPage() {
        return Number(this.button?.dataset.nextPage) || 0;
      }

      // Current location (so active filters and sorting are kept) with another page and, optionally, the section id.
      buildUrl(page, sectionId) {
        const url = new URL(window.location.href);
        url.hash = '';
        url.searchParams.set('page', String(page));
        if (sectionId) url.searchParams.set('section_id', sectionId);
        else url.searchParams.delete('section_id');
        return url;
      }

      // The server href can carry a section_id after a facets re-render: point it at the clean page URL instead.
      syncHref() {
        if (this.button && this.nextPage) this.button.href = this.buildUrl(this.nextPage).toString();
      }

      setLoading(isLoading) {
        this.loading = isLoading;
        this.setAttribute('aria-busy', String(isLoading));
        const button = this.button;
        if (!button) return;
        button.classList.toggle('is-loading', isLoading);
        button.toggleAttribute('aria-disabled', isLoading);
        button.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      }

      async loadMore() {
        const page = this.nextPage;
        if (this.loading || !page) return;
        const fallbackUrl = this.buildUrl(page).toString();

        this.setLoading(true);
        try {
          const response = await fetch(this.buildUrl(page, this.dataset.sectionId).toString());
          if (!response.ok) throw new Error(`Load more failed with status ${response.status}`);

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          // The grid was re-rendered (filters) while the request was in flight: this element is stale.
          if (!this.isConnected) return;

          const grid = document.getElementById(this.dataset.grid);
          const incomingGrid = doc.getElementById(this.dataset.grid);
          if (!grid || !incomingGrid) throw new Error('Load more response has no product grid');

          const items = Array.from(incomingGrid.children);
          items.forEach((item, index) => {
            item.classList.add('is-new');
            item.style.setProperty('--lm-index', String(index));
            item.addEventListener('animationend', () => item.classList.remove('is-new'), { once: true });
          });
          grid.append(...items);
          items[0]?.querySelector('a')?.focus({ preventScroll: true });

          this.applyState(doc.querySelector('collection-load-more'));
        } catch (error) {
          console.error(error);
          // Fall back to the plain paginated page, the same as without JavaScript.
          window.location.assign(fallbackUrl);
          return;
        }
        this.setLoading(false);
      }

      // Copy the progress, the text and the next page from the freshly rendered footer.
      applyState(incoming) {
        const action = this.querySelector('[data-lm-action]');
        const incomingButton = incoming?.querySelector('[data-lm-button]');

        if (incoming) {
          const text = this.querySelector('[data-lm-text]');
          const incomingText = incoming.querySelector('[data-lm-text]');
          if (text && incomingText) text.textContent = incomingText.textContent;

          const fill = this.querySelector('[data-lm-fill]');
          const incomingFill = incoming.querySelector('[data-lm-fill]');
          if (fill && incomingFill) fill.style.width = incomingFill.style.width;

          const progress = this.querySelector('[data-lm-progress]');
          const incomingProgress = incoming.querySelector('[data-lm-progress]');
          if (progress && incomingProgress) progress.setAttribute('aria-valuenow', incomingProgress.getAttribute('aria-valuenow'));
        }

        if (incomingButton && this.button) {
          this.button.dataset.nextPage = incomingButton.dataset.nextPage;
          this.syncHref();
        } else if (action) {
          // Last page loaded: the button goes away, the progress stays.
          action.hidden = true;
        }
      }
    }
  );
}

if (!customElements.get('collection-filter-fab')) {
  customElements.define(
    'collection-filter-fab',
    class CollectionFilterFab extends HTMLElement {
      connectedCallback() {
        const root = this.closest('[data-collection-grid]');
        this.summary = root?.querySelector('.mobile-facets__open-wrapper');
        this.details = root?.querySelector('details.mobile-facets__disclosure');
        this.grid = root?.querySelector('#ProductGridContainer');
        this.button = this.querySelector('[data-fab-button]');
        if (!this.summary || !this.grid || !this.button) return;

        this.state = { toolbarPassed: false, gridVisible: false, drawerOpen: false };
        this.restoreY = null;

        this.onClick = () => {
          // Dawn focuses the toolbar button when the drawer opens and closes, which would scroll the page back up.
          this.restoreY = window.scrollY;
          this.summary.click();
        };
        this.onSummaryFocus = () => {
          if (this.restoreY !== null) window.scrollTo({ top: this.restoreY, behavior: 'instant' });
        };
        this.onToggle = () => {
          if (!this.details.open) this.restoreY = null;
        };
        this.button.addEventListener('click', this.onClick);
        this.summary.addEventListener('focus', this.onSummaryFocus);
        this.details?.addEventListener('toggle', this.onToggle);

        // Shown once the toolbar button has scrolled past the top while the grid is still in view.
        this.toolbarObserver = new IntersectionObserver(([entry]) => {
          this.state.toolbarPassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          this.render();
        });
        this.gridObserver = new IntersectionObserver(([entry]) => {
          this.state.gridVisible = entry.isIntersecting;
          this.render();
        });
        this.toolbarObserver.observe(this.summary);
        this.gridObserver.observe(this.grid);

        if (this.details) {
          this.drawerObserver = new MutationObserver(() => this.syncDrawer());
          this.drawerObserver.observe(this.details, { attributes: true, attributeFilter: ['open', 'class'] });
          this.syncDrawer();
        }
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onClick);
        this.summary?.removeEventListener('focus', this.onSummaryFocus);
        this.details?.removeEventListener('toggle', this.onToggle);
        this.toolbarObserver?.disconnect();
        this.gridObserver?.disconnect();
        this.drawerObserver?.disconnect();
      }

      syncDrawer() {
        this.state.drawerOpen = this.details.hasAttribute('open') || this.details.classList.contains('menu-opening');
        this.render();
      }

      render() {
        const { toolbarPassed, gridVisible, drawerOpen } = this.state;
        this.classList.toggle('is-visible', toolbarPassed && gridVisible && !drawerOpen);
      }
    }
  );
}
